import * as React from 'react';
import Step, { PrivateStepState, SharedStepState, StepProperties } from '../Step';
import { Providers, Region, RegionContinent, RegionCountry, RegionFlag } from 'portal-shared-lib';
import BucketNameProvision from './BucketNameProvision';
import { CircledExclamationMarkIcon, RightArrowIcon } from '../../common/Icons';

interface RegionStepComponentProperties extends StepProperties<RegionSharedStepState> {
    provider: string;
    disabledRegions: Array<string>;
    regions: Region[];
    storageContainerName: string;
    isLucidLink: boolean;
}

export interface RegionSharedStepState extends SharedStepState, BucketNameProvision {
    providerRegion: Region | null;
}

type RegionsMap = {
    [key: string]: Array<Region> // key: string - continent name
};

interface RegionPrivateStepState extends PrivateStepState {}
interface RegionStepState extends RegionPrivateStepState, RegionSharedStepState {}

const GREYED_OUT_REGION_OPACITY = 0.3;

interface HoverMessageOptions {
    isSingleRegionDataCenter: boolean;
    isComingSoon: boolean;
    isDisabled: boolean;
}

const sortRegions = (regions: Region[]): Region[] => {
    return regions.sort((a, b) => {
        if (a.name > b.name) { return 1; }
        if (a.name < b.name) { return -1; }
        return 0;
    });
};

class RegionStepComponent extends Step<RegionStepComponentProperties, RegionStepState, RegionSharedStepState> {

    constructor(props: any) {
        super (props);

        this.handleAdvancedSettingsClick = this.handleAdvancedSettingsClick.bind(this);
        this.selectRegion = this.selectRegion.bind(this);
        this.getContinentElements = this.getContinentElements.bind(this);
        this.buildContinentRegionsHtml = this.buildContinentRegionsHtml.bind(this);
        this.buildRegionElement = this.buildRegionElement.bind(this);
        RegionStepComponent.getIbmSingleDataCenterMessage = RegionStepComponent.getIbmSingleDataCenterMessage.bind(this);
        RegionStepComponent.getComingSoonMessage = RegionStepComponent.getComingSoonMessage.bind(this);
        RegionStepComponent.getDisabledRegionMessage = RegionStepComponent.getDisabledRegionMessage.bind(this);
        this.getRegionHoverMessage = this.getRegionHoverMessage.bind(this);
        this.buildRegionClickHandler = this.buildRegionClickHandler.bind(this);
        this.mapRegionsToContinents = this.mapRegionsToContinents.bind(this);

        this.state = this.props.savedState;
    }

    protected getSharedStepState(): RegionSharedStepState {
        return {
            providerRegion: this.state.providerRegion,
            ownBucketNameProvided: this.state.ownBucketNameProvided,
        };
    }

    render() {
        return (
            <div className="col-12 col-lg-6 col-xl-6 offset-lg-1 d-flex align-items-center region-step">
                <form className="radio__list">
                    <div>
                        {this.getContinentElements()}
                    </div>
                    <div className="col-12 bottom-section">
                        {this.getAdvancedSettingsCheckbox()}
                    </div>
                    <button disabled={!this.state.providerRegion} type="button" className="btn step-btn float-left" onClick={this.completeStep}>
                        <span>Next</span>
                        <RightArrowIcon width="13px" height="13px" />
                    </button>
                </form>
            </div>
        );
    }

    private handleAdvancedSettingsClick(e: any): void {
        this.setState({ ownBucketNameProvided: e.target.checked });
    }

    private selectRegion(region: Region): void {
        this.setState({ providerRegion: region });
    }

    private getContinentElements(): JSX.Element[] {
        const continentRegionsMap = this.mapRegionsToContinents();
        const continents = Object.keys(continentRegionsMap);
        continents.sort();
        const result: JSX.Element[] = [];
        for (const continent of continents) {
            const regions = continentRegionsMap[continent];
            result.push(
                <div className="location" key={continent}>
                    <div>
                        {continent}
                    </div>
                    <ul>
                        {this.buildContinentRegionsHtml(continent, regions)}
                    </ul>
                </div>
            );
        }
        return result;
    }

    private getAdvancedSettingsCheckbox(): JSX.Element {
        if (this.props.isLucidLink && this.props.provider === Providers.AWS) { 
            return (<div className="checkbox">&nbsp;</div>);
        } else {
            return (
                <div className="checkbox">
                    <input type="checkbox" name="bucketname" id="bucketname" onChange={this.handleAdvancedSettingsClick} checked={this.state.ownBucketNameProvided} />
                    <label htmlFor="bucketname">Configure advanced settings</label>
                </div>
            );
        }
    }

    private buildContinentRegionsHtml(continent: string, regions: Region[]): JSX.Element[] {
        if (regions.length < 1) { return []; }

        const sortedRegions = RegionStepComponent.sortContinentRegions(continent, regions);

        const htmlRegions: Array<JSX.Element> = [];
        for (const region of sortedRegions) {
            const htmlRegion = this.buildRegionElement(region);
            htmlRegions.push(htmlRegion);
        }
        return htmlRegions;
    }

    private static sortContinentRegions(continent: string, regions: Region[]): Region[] {
        if (continent === RegionContinent.NorthAmerica) {
            const canadaRegions = regions.filter(x => x.country === RegionCountry.Canada);
            const usaRegions = regions.filter(x => x.country === RegionCountry.Usa);
            const other = regions.filter((x) => {
                if (!x.country) {
                    return true;
                }

                return ![ RegionCountry.Canada, RegionCountry.Usa ].includes(x.country);
            });

            const sortedAmericaRegions = [
                ...sortRegions(canadaRegions),
                ...sortRegions(usaRegions),
                ...sortRegions(other)
            ];
            return sortedAmericaRegions;
        }

        const sortedRegions = sortRegions(regions);
        return sortedRegions;
    }

    private buildRegionElement(region: Region): JSX.Element {
        const isRegionDisabled = Boolean(this.props.disabledRegions.find(x => x === region.name));

        // TODO ComingSoon filter at parent component based on "isLucidLink" and remove "isLucidLink" dependency
        const flags = region.flags ? region.flags : [];

        const isComingSoon = this.props.isLucidLink && flags.includes(RegionFlag.ComingSoon);
        const isSingleRegionDataCenter =
            this.props.provider === Providers.IBM && flags.includes(RegionFlag.IbmSingleDataCenter);
        const isSelected = this.state.providerRegion ? this.state.providerRegion.name === region.name : false;

        const shouldGreyOutRegion = isRegionDisabled || isComingSoon;
        const hoverMessage = this.getRegionHoverMessage({
            isSingleRegionDataCenter: isSingleRegionDataCenter,
            isComingSoon,
            isDisabled: isRegionDisabled
        });
        const buttonOpacity = { opacity: shouldGreyOutRegion ? GREYED_OUT_REGION_OPACITY : 1 };
        return (
            <li
                key={region.name}
                onClick={this.buildRegionClickHandler(region, shouldGreyOutRegion)}
                className={hoverMessage ? 'ttip' : ''}
            >
                <input
                    readOnly={true}
                    type="radio"
                    name="region"
                    checked={isSelected}
                    value={region.name}
                    id={region.name}
                    style={buttonOpacity}
                    disabled={shouldGreyOutRegion}
                />
                <label style={buttonOpacity} htmlFor={region.name}>
                    {region.name}
                    {hoverMessage && !shouldGreyOutRegion && <CircledExclamationMarkIcon width="11px" />}
                </label>

                {hoverMessage}
            </li>
        );
    }

    private static getIbmSingleDataCenterMessage(): JSX.Element {
        const ibmLink = 'https://cloud.ibm.com/docs/cloud-object-storage?topic=cloud-object-storage-endpoints';
        return (
            <div>
                <strong>Note:</strong> This region is supported by a single data center and offers no
                resiliency by IBM in case of outage. <a target="_blank" href={ibmLink}>Read more</a>
            </div>
        );
    }

    private static getComingSoonMessage(): JSX.Element {
        return (
            <div>
                Coming soon.
            </div>
        );
    }

    private static getDisabledRegionMessage(): JSX.Element {
        return (
            <div>
                Region temporarily disabled. Storage vendor does not meet LucidLink's performance profile required
                for production workloads.
            </div>
        );
    }

    private getRegionHoverMessage(options: HoverMessageOptions): JSX.Element | null {
        if (options.isDisabled) {
            return RegionStepComponent.getDisabledRegionMessage();
        }

        if (options.isComingSoon) {
            return RegionStepComponent.getComingSoonMessage();
        }

        // Currently we support single data center regions only for IBM.
        if (options.isSingleRegionDataCenter) {
            return RegionStepComponent.getIbmSingleDataCenterMessage();
        }

        return null;
    }

    private buildRegionClickHandler(region: Region, isDisabled: boolean): () => void {
        return () => {
            if (isDisabled) { return; }
            this.selectRegion(region);
        };
    }

    private mapRegionsToContinents(): RegionsMap {
        let continents = this.props.regions.map((region) => {
            return region.continent;
        });

        let regionsMap: RegionsMap = {};
        for (const continent of continents) {
            if (regionsMap.hasOwnProperty(continent)) {
                continue;
            }

            const continentRegions = this.props.regions.filter((region) => {
                return region.continent === continent;
            });
            if (continentRegions.length > 0) {
                regionsMap[continent] = continentRegions;
            }
        }

        return regionsMap;
    }
}

export default RegionStepComponent;



// WEBPACK FOOTER //
// ./src/webportal/components/wizard/components/RegionStep.tsx